import { useState, useRef, useEffect, useCallback } from "react";
import { SquarePen, Volume2, VolumeX } from "lucide-react";
import { createChatRequestId, doppelgangerSse } from "../../services/sse";
import { notificationStream } from "../../services/notifications";
import { chatApi } from "../../services/chatApi";
import { useAuth } from "../../hooks/useAuth";
import EmojiPicker from "../EmojiPicker/EmojiPicker";
import MessageBubble, { groupActionNotes } from "./MessageBubble";
import AnnaIdentity from "./AnnaIdentity";
import {
  stripActions as stripActionsShared,
  extractArtifacts as extractArtifactsShared,
} from "../../utils/messageHelpers";
import {
  clipboardHasInlineText,
  extractClipboardFiles,
  getAttachmentContentType,
  mergeChatAttachments,
  readClipboardFiles,
  uploadChatAttachments,
} from "../../utils/chatAttachments";
import {
  collectPendingTaskIds,
  isFreshChatSession,
  dedupeHistoryMessages,
  historyContainsTaskResult,
} from "../../utils/chatPolling";
import { finalizeTerminalErrorMessages } from "../../utils/chatStreamState";
import "./Chat.css";

const CLOUD_POLL_INTERVAL_MS = 3000;
const CLOUD_POLL_MAX_MS = 15 * 60 * 1000;

const CHAT_PROFILES = {
  doppelganger: {
    assistantName: "Anna",
    activeSessionKey: "anna_activeSessionId",
    lastActivityKey: "anna_lastActivityTs",
    transport: doppelgangerSse,
    notifications: false,
  },
};

function draftKey(profile, sessionId) {
  return `anna_chat_draft:${profile}:${sessionId || "new"}`;
}

function readDraft(profile, sessionId) {
  try {
    const scoped = localStorage.getItem(draftKey(profile, sessionId));
    const raw = scoped || (profile === "anna" ? localStorage.getItem("anna_chat_draft") : null);
    const saved = raw ? JSON.parse(raw) : null;
    if (saved && Date.now() - saved.ts < 3600000) return saved.text;
  } catch {}
  return "";
}

function Chat({ profile = "doppelganger" }) {
  const profileConfig = CHAT_PROFILES[profile] || CHAT_PROFILES.doppelganger;
  const { assistantName, activeSessionKey, lastActivityKey, transport, notifications } = profileConfig;
  const isDoppelganger = transport === doppelgangerSse;
  const initialSession = localStorage.getItem(activeSessionKey) || null;
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(() => readDraft(profile, initialSession));
  const [isStreaming, setIsStreaming] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isAwaitingDetached, setIsAwaitingDetached] = useState(false);
  const [sessions, setSessions] = useState(() => chatApi.getCachedSessions());
  const [activeSession, setActiveSession] = useState(initialSession);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth > 768);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => localStorage.getItem("anna_voice_enabled") === "true");
  const [isSpeaking, setIsSpeaking] = useState(false);
  // Chat is Gemma-only — messages stream from the on-box anna-chat SSE service.
  const [gemmaThink, setGemmaThink] = useState(
    () => localStorage.getItem("anna_gemma_think") !== "false"
  );
  const gemmaThinkRef = useRef(gemmaThink);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const streamBuffer = useRef("");
  const streamBuffers = useRef(new Map());
  const activeSessionRef = useRef(initialSession);
  const lastActivityRef = useRef(Number(localStorage.getItem(lastActivityKey)) || Date.now());
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const voiceEnabledRef = useRef(voiceEnabled);
  const isPaginatingRef = useRef(false);
  // Skip the next history reload after a stream finishes — we already have
  // correct messages and reloading would wipe ephemeral action_result notes.
  const skipHistoryReloadRef = useRef(false);
  // When true, the next messages-effect auto-scrolls unconditionally
  // (initial load, session switch, send). Reset by manual scroll-up.
  const shouldSnapRef = useRef(true);
  const cloudPollTimerRef = useRef(null);
  const cloudPollStartedAtRef = useRef(0);
  const cloudPollSessionRef = useRef(null);
  const cloudPollGenerationRef = useRef(0);
  const cloudPollExhaustedSessionRef = useRef(null);
  const pendingTaskIdsRef = useRef(new Set());
  const chatMountedRef = useRef(true);

  function stopCloudPolling({ clearPending = false } = {}) {
    cloudPollGenerationRef.current += 1;
    if (cloudPollTimerRef.current) window.clearTimeout(cloudPollTimerRef.current);
    cloudPollTimerRef.current = null;
    cloudPollStartedAtRef.current = 0;
    cloudPollSessionRef.current = null;
    if (clearPending) pendingTaskIdsRef.current.clear();
  }

  // Keep ref + localStorage in sync with state
  const updateActiveSession = (id, preserveActivity) => {
    const sessionChanged = activeSessionRef.current !== id;
    if (sessionChanged) {
      stopCloudPolling({ clearPending: true });
      cloudPollExhaustedSessionRef.current = null;
    }
    setActiveSession(id);
    activeSessionRef.current = id;
    if (id) localStorage.setItem(activeSessionKey, id);
    else localStorage.removeItem(activeSessionKey);
    if (sessionChanged) setInput(readDraft(profile, id));
    if (!preserveActivity) {
      const now = Date.now();
      lastActivityRef.current = now;
      localStorage.setItem(lastActivityKey, String(now));
    }
  };

  const saveDraft = (text, sessionId = activeSessionRef.current) => {
    const key = draftKey(profile, sessionId);
    if (text) {
      const value = JSON.stringify({ text, ts: Date.now() });
      localStorage.setItem(key, value);
      if (profile === "anna") localStorage.setItem("anna_chat_draft", value);
    } else {
      localStorage.removeItem(key);
      if (profile === "anna") localStorage.removeItem("anna_chat_draft");
    }
  };

  const toggleVoice = () => {
    const next = !voiceEnabled;
    setVoiceEnabled(next);
    voiceEnabledRef.current = next;
    localStorage.setItem("anna_voice_enabled", String(next));
    if (next) {
      // Unlock audio playback with a silent sound on user gesture
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
      ctx.resume();
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsSpeaking(false);
    }
  };

  const playTTS = async (text) => {
    if (!text.trim()) return;
    setIsSpeaking(true);
    try {
      const API_BASE = import.meta.env.VITE_API_URL || "/api";
      const tkn = localStorage.getItem("anna_token");
      // Migrate removed or unknown provider preferences to local F5-TTS.
      const savedProvider = localStorage.getItem("anna_tts_provider");
      const provider = savedProvider === "google" ? "google" : "f5";
      const res = await fetch(`${API_BASE}/voice`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) },
        body: JSON.stringify({ text, provider }),
      });
      if (!res.ok) {
        console.error("TTS request failed:", res.status);
        setIsSpeaking(false);
        return;
      }
      const data = await res.json();
      if (data.audio) {
        const audioBytes = Uint8Array.from(atob(data.audio), (c) => c.charCodeAt(0));
        const blob = new Blob([audioBytes], { type: data.format === "wav" ? "audio/wav" : "audio/mpeg" });
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => { setIsSpeaking(false); URL.revokeObjectURL(url); };
        audio.onerror = (e) => { console.error("Audio play error:", e); setIsSpeaking(false); URL.revokeObjectURL(url); };
        await audio.play().catch((e) => {
          console.error("Audio play blocked:", e);
          setIsSpeaking(false);
          URL.revokeObjectURL(url);
        });
      } else {
        console.error("TTS response:", data);
        setIsSpeaking(false);
      }
    } catch (err) {
      console.error("TTS failed:", err);
      setIsSpeaking(false);
    }
  };

  // Apply a history page (REST result shaped like the old WS "history" frame)
  // to the message list. `paginating` indicates an older-page prepend.
  function applyHistoryFrame(data) {
    const stripActions = stripActionsShared;
    const extractArtifacts = (m) => {
      if (m.role !== "assistant") return undefined;
      const out = extractArtifactsShared(m.actionsResults);
      return out.length ? out : undefined;
    };
    const sourceMessages = dedupeHistoryMessages(data.messages);
    if (isDoppelganger) {
      sourceMessages.forEach((message) => {
        collectPendingTaskIds(message.actionsResults).forEach((taskId) => {
          pendingTaskIdsRef.current.add(taskId);
        });
      });
      for (const taskId of pendingTaskIdsRef.current) {
        if (historyContainsTaskResult(sourceMessages, taskId)) {
          pendingTaskIdsRef.current.delete(taskId);
        }
      }
    }
    const history = sourceMessages.flatMap((m) => {
      const mapped = {
        role: m.role,
        content: m.role === "assistant" ? stripActions(m.content) : m.content,
        timestamp: m.timestamp,
        sessionId: m.sessionId,
        kind: m.kind || "conversation",
        ...(m.messageId ? { messageId: m.messageId } : {}),
        ...(m.requestId ? { requestId: m.requestId } : {}),
        ...(m.source ? { source: m.source } : {}),
        ...(m.metadata ? { metadata: m.metadata } : {}),
        ...(m.attachments?.length ? { attachments: m.attachments } : {}),
        ...(extractArtifacts(m) ? { artifacts: extractArtifacts(m) } : {}),
      };
      // Reconstruct inline action_result notes from persisted actionsResults
      if (m.role === "assistant" && Array.isArray(m.actionsResults) && m.actionsResults.length) {
        const notes = m.actionsResults.map((a) => ({
          role: "system",
          actionNote: true,
          action: a.action || "unknown",
          success: a.success !== false,
          content: `Action: ${a.action || "unknown"} — ${a.success ? "Done" : "Failed"}`,
          ...(Array.isArray(a.images) && a.images.length ? { images: a.images } : {}),
          timestamp: m.timestamp,
        }));
        return [...notes, mapped];
      }
      return [mapped];
    });
    setHasMore(!!data.hasMore);
    setCursor(data.cursor || null);
    setLoadingMore(false);
    setIsAwaitingDetached(false);

    setMessages((prev) => {
      // If this is a "load more" (user scrolled up) → prepend
      if (isPaginatingRef.current && prev.length > 0) {
        isPaginatingRef.current = false;
        // Preserve scroll position after prepend
        const container = messagesContainerRef.current;
        const prevHeight = container?.scrollHeight || 0;
        requestAnimationFrame(() => {
          if (container) container.scrollTop = container.scrollHeight - prevHeight;
        });
        const streamingMsg = prev.find((m) => m.streaming);
        return streamingMsg ? [...history, ...prev.filter((m) => !m.streaming), streamingMsg] : [...history, ...prev];
      }
      // Initial load — replace
      isPaginatingRef.current = false;
      shouldSnapRef.current = true;
      const streamingMsg = prev.find((m) => m.streaming);
      if (streamingMsg) {
        return [...history, streamingMsg];
      }
      return history;
    });
    setIsThinking(false);
  }

  function showActiveTurnPending(message) {
    setIsStreaming(false);
    setIsThinking(true);
    setIsAwaitingDetached(true);
    streamBuffer.current = "";
    setMessages((prev) => {
      const withoutTransient = prev.filter((m) => !m.streaming && !m.detachedNote);
      return [
        ...withoutTransient,
        {
          role: "system",
          detachedNote: true,
          content: message || `${assistantName} is still working - the reply will appear here when it's ready.`,
          timestamp: new Date().toISOString(),
        },
      ];
    });
  }

  function showDelegatedTaskPending() {
    setIsAwaitingDetached(false);
    setIsThinking(false);
    setMessages((prev) => prev.filter((message) => !message.detachedNote));
  }

  function rememberPendingTasks(actions) {
    const taskIds = collectPendingTaskIds(actions);
    taskIds.forEach((taskId) => pendingTaskIdsRef.current.add(taskId));
    return taskIds.length > 0;
  }

  async function fetchAuthoritativeHistory(sessionId) {
    const data = await chatApi.fetchHistory(sessionId, { limit: 20 });
    if (!chatMountedRef.current || activeSessionRef.current !== sessionId) return null;
    applyHistoryFrame(data);
    for (const taskId of pendingTaskIdsRef.current) {
      if (historyContainsTaskResult(data.messages, taskId)) {
        pendingTaskIdsRef.current.delete(taskId);
      }
    }
    return data;
  }

  function startCloudPolling(sessionId) {
    if (!isDoppelganger || !token || !sessionId || activeSessionRef.current !== sessionId) return;
    if (cloudPollExhaustedSessionRef.current === sessionId) return;
    if (cloudPollSessionRef.current === sessionId && cloudPollTimerRef.current) return;

    stopCloudPolling();
    const generation = cloudPollGenerationRef.current;
    cloudPollSessionRef.current = sessionId;
    cloudPollStartedAtRef.current = Date.now();

    const poll = async () => {
      if (
        generation !== cloudPollGenerationRef.current
        || activeSessionRef.current !== sessionId
      ) return;

      let status;
      try {
        status = await transport.activeTurn(sessionId);
        if (generation !== cloudPollGenerationRef.current) return;
        status.pendingTaskIds?.forEach((taskId) => pendingTaskIdsRef.current.add(taskId));
        if (status.pendingTasksKnown && !status.pendingTasks) pendingTaskIdsRef.current.clear();

        if (!transport._streamingActive && (!status.active || status.pendingTasks || pendingTaskIdsRef.current.size)) {
          await fetchAuthoritativeHistory(sessionId);
          if (status.pendingTasksKnown && !status.pendingTasks) pendingTaskIdsRef.current.clear();
        }
      } catch (err) {
        console.error("cloud turn poll failed:", err);
      }

      if (
        generation !== cloudPollGenerationRef.current
        || activeSessionRef.current !== sessionId
      ) return;

      const pending = status?.pendingTasks || pendingTaskIdsRef.current.size > 0;
      const shouldContinue = Boolean(status?.active || pending);
      const withinLimit = Date.now() - cloudPollStartedAtRef.current < CLOUD_POLL_MAX_MS;
      if (shouldContinue && withinLimit) {
        if (!transport._streamingActive) {
          if (status?.active) showActiveTurnPending();
          else showDelegatedTaskPending();
        }
        cloudPollTimerRef.current = window.setTimeout(poll, CLOUD_POLL_INTERVAL_MS);
        return;
      }

      const timedOut = shouldContinue && !withinLimit;
      if (timedOut) cloudPollExhaustedSessionRef.current = sessionId;
      else cloudPollExhaustedSessionRef.current = null;
      stopCloudPolling({ clearPending: true });
      setIsAwaitingDetached(false);
      setIsThinking(false);
      try {
        await fetchAuthoritativeHistory(sessionId);
        if (!timedOut) await loadSessions();
      } catch (err) {
        console.error("cloud turn settlement refresh failed:", err);
      }
    };

    void poll();
  }

  async function refreshActiveTurn(sessionId) {
    if (!sessionId) return false;
    try {
      const status = await transport.activeTurn(sessionId);
      status.pendingTaskIds?.forEach((taskId) => pendingTaskIdsRef.current.add(taskId));
      if (status.pendingTasksKnown && !status.pendingTasks) pendingTaskIdsRef.current.clear();
      if (isDoppelganger && activeSessionRef.current === sessionId
          && (status?.active || status?.pendingTasks || pendingTaskIdsRef.current.size)) {
        if (status.active) showActiveTurnPending();
        else showDelegatedTaskPending();
        startCloudPolling(sessionId);
        return true;
      }
    } catch (err) {
      console.error("active turn check failed:", err);
    }
    return false;
  }

  // Load a page of history for a session over REST. Hydrates from the
  // localStorage cache first (stale-while-revalidate) on the initial page.
  async function loadHistory(sessionId, { before } = {}) {
    if (!sessionId || activeSessionRef.current !== sessionId) return;
    if (before) {
      isPaginatingRef.current = true;
    } else {
      const cached = chatApi.getCachedHistory(sessionId);
      if (cached && activeSessionRef.current === sessionId) applyHistoryFrame(cached);
    }
    try {
      const data = await chatApi.fetchHistory(sessionId, { limit: 10, before });
      if (activeSessionRef.current !== sessionId) return;
      applyHistoryFrame(data);
      if (!before) await refreshActiveTurn(sessionId);
    } catch (err) {
      console.error("loadHistory failed:", err);
      setLoadingMore(false);
      isPaginatingRef.current = false;
    }
  }

  // Only a visible chat's open/focus check may retire its selected session.
  async function applySessionsFrame(list, checkFreshness = false) {
    setSessions(list);
    if (!checkFreshness || transport._streamingActive || isAwaitingDetached) return;
    const currentId = activeSessionRef.current;
    if (!currentId) return;
    const current = list.find((session) => session.id === currentId);

    if (isFreshChatSession(current)) {
      // Already attached to a known session — keep it, refresh messages.
      // But skip if we just finished streaming (messages are already up to date
      // and reloading would wipe ephemeral action_result system notes).
      if (skipHistoryReloadRef.current) {
        skipHistoryReloadRef.current = false;
      } else {
        loadHistory(currentId);
      }
    } else {
      try {
        const turn = await transport.activeTurn(currentId);
        if (turn?.active || turn?.pendingTasks || activeSessionRef.current !== currentId) return;
      } catch (error) {
        console.error("focus active-turn check failed:", error);
        return;
      }
      // No fresh session — empty new chat (id minted on first send).
      updateActiveSession(null);
      setMessages([]);
    }
  }

  // Refresh the sessions list over REST (replaces the old WS listSessions).
  async function loadSessions({ checkFreshness = false } = {}) {
    const selectedAtRequest = activeSessionRef.current;
    try {
      const list = await chatApi.fetchSessions();
      if (selectedAtRequest === activeSessionRef.current) await applySessionsFrame(list, checkFreshness);
    } catch (err) {
      console.error("loadSessions failed:", err);
    }
  }

  async function reconcileTerminalError(sessionId, frame) {
    const results = await Promise.allSettled([
      sessionId ? fetchAuthoritativeHistory(sessionId) : Promise.resolve(null),
      chatApi.fetchSessions(),
    ]);
    const [historyResult, sessionsResult] = results;

    if (historyResult.status === "rejected") {
      console.error("terminal error history refresh failed:", historyResult.reason);
    } else if (historyResult.value && activeSessionRef.current === sessionId) {
      setMessages((prev) => finalizeTerminalErrorMessages(prev, frame));
    }
    if (sessionsResult.status === "rejected") {
      console.error("terminal error sessions refresh failed:", sessionsResult.reason);
    } else if (chatMountedRef.current) {
      setSessions(sessionsResult.value);
    }
  }

  // Handle incoming streaming/notification frames (SSE + notification stream)
  const handleWsMessage = useCallback((data) => {
    // ── Session-scoping guard ──
    // Streaming frames, including terminal errors without a trailing `done`, may arrive
    // from a request that was initiated in a different session. Since the data
    // is persisted server-side regardless, we can safely discard frames that
    // don't belong to the session currently displayed in the view.
    const streamingTypes = new Set(["typing", "checking", "chunk", "done", "error", "action_result", "stream_detached", "active_turn"]);
    if (streamingTypes.has(data.type)) {
      const frameSession = data._sseSessionId || data.sessionId;
      const currentSession = activeSessionRef.current;
      if (frameSession && currentSession && frameSession !== currentSession) {
        return;
      }
    }

    if (data.type === "typing") {
      // Anna is processing — show thinking indicator
      setIsThinking(true);
      setIsStreaming(true);
      setIsAwaitingDetached(false);
      if (data.requestId) streamBuffers.current.set(data.requestId, "");
    } else if (data.type === "checking") {
      // Anna is fetching schedule/reminder data — show inline status
      setIsThinking(true);
      setIsStreaming(true);
      setIsAwaitingDetached(false);
      if (data.requestId && !streamBuffers.current.has(data.requestId)) {
        streamBuffers.current.set(data.requestId, "");
      }
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findLastIndex((m) => m.streaming && (!data.requestId || m.requestId === data.requestId));
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            content: "",
            checking: data.message || "Checking the agenda...",
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : {}),
          };
        } else {
          // Create a new streaming placeholder with checking indicator
          updated.push({
            role: "assistant",
            content: "",
            timestamp: new Date().toISOString(),
            streaming: true,
            checking: data.message || "Checking the agenda...",
            requestId: data.requestId,
            sessionId: data.sessionId,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : isDoppelganger ? { metadata: { assistantId: "doppelganger" } } : {}),
          });
        }
        return updated;
      });
    } else if (data.type === "chunk") {
      setIsThinking(false);
      setIsAwaitingDetached(false);
      const previousContent = data.requestId
        ? streamBuffers.current.get(data.requestId) || ""
        : streamBuffer.current;
      const rawContent = previousContent + (data.content || "");
      if (data.requestId) streamBuffers.current.set(data.requestId, rawContent);
      else streamBuffer.current = rawContent;
      // Strip ```action...``` blocks from displayed content
      const displayContent = rawContent.replace(/```action\s*\n[\s\S]*?```\s*/g, "").replace(/\n{3,}/g, "\n\n").trimEnd();
      setMessages((prev) => {
        const updated = [...prev];
        // A delegated callback can overlap another live turn. Bind by request.
        const idx = updated.findLastIndex((m) => m.streaming && (!data.requestId || m.requestId === data.requestId));
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            content: displayContent,
            checking: null,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : {}),
          };
        } else {
          updated.push({
            role: "assistant",
            content: displayContent,
            timestamp: new Date().toISOString(),
            streaming: true,
            requestId: data.requestId,
            sessionId: data.sessionId,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : isDoppelganger ? { metadata: { assistantId: "doppelganger" } } : {}),
          });
        }
        return updated;
      });
    } else if (data.type === "done") {
      setIsStreaming(false);
      setIsThinking(false);
      setIsAwaitingDetached(false);
      const rawContent = data.requestId
        ? streamBuffers.current.get(data.requestId) || ""
        : streamBuffer.current;
      const finalContent = stripActionsShared(rawContent);
      // Pull any HTML artifacts produced by generate_html_file actions so we
      // can render an inline download card under the assistant reply.
      const artifacts = extractArtifactsShared(data.actions);
      // Notify Notebook component if any page-related action succeeded
      const pageActions = ["attach_to_page", "update_page_section", "create_page", "append_to_page"];
      if ((data.actions || []).some((a) => a?.success && pageActions.includes(a?.action))) {
        window.dispatchEvent(new CustomEvent("anna-page-updated"));
      }
      setMessages((prev) => {
        const updated = [...prev];
        const idx = updated.findLastIndex((m) => m.streaming && (!data.requestId || m.requestId === data.requestId));
        if (idx >= 0) {
          updated[idx] = {
            ...updated[idx],
            content: finalContent,
            streaming: false,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : {}),
            ...(artifacts.length ? { artifacts } : {}),
          };
        } else if (artifacts.length) {
          updated.push({
            role: "assistant",
            content: finalContent,
            timestamp: new Date().toISOString(),
            requestId: data.requestId,
            sessionId: data.sessionId,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            ...(data.metadata ? { metadata: data.metadata } : isDoppelganger ? { metadata: { assistantId: "doppelganger" } } : {}),
            artifacts,
          });
        }
        return updated;
      });
      if (data.requestId) streamBuffers.current.delete(data.requestId);
      else streamBuffer.current = "";
      const hasPendingTasks = isDoppelganger && rememberPendingTasks(data.actions);
      // Play TTS if voice is enabled
      if (voiceEnabledRef.current && finalContent) {
        playTTS(finalContent);
      }
      // Refresh sessions list (new messages may update preview)
      skipHistoryReloadRef.current = true;
      loadSessions();
      if (hasPendingTasks) {
        cloudPollExhaustedSessionRef.current = null;
        showDelegatedTaskPending();
        startCloudPolling(activeSessionRef.current);
      }
    } else if (data.type === "message") {
      setIsThinking(false);
      setIsAwaitingDetached(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.content || data.message,
          timestamp: new Date().toISOString(),
          requestId: data.requestId,
          ...(data.messageId ? { messageId: data.messageId } : {}),
          ...(data.metadata ? { metadata: data.metadata } : isDoppelganger ? { metadata: { assistantId: "doppelganger" } } : {}),
        },
      ]);
    } else if (data.type === "action_result") {
      if (isDoppelganger) rememberPendingTasks([data]);
      setMessages((prev) => [
        ...prev,
        {
          role: "system",
          actionNote: true,
          action: data.action || "action",
          success: data.success !== false,
          content: data.message || `Action: ${data.action} — ${data.success ? "Done" : "Failed"}`,
          ...(Array.isArray(data.images) && data.images.length ? { images: data.images } : {}),
          timestamp: new Date().toISOString(),
        },
      ]);
    } else if (data.type === "stream_detached" || data.type === "active_turn") {
      // The HTTP stream died (tab backgrounded / network blip) but the server
      // keeps processing detached. Drop the streaming placeholder and show a
      // subtle note — the reply arrives via a `notification` frame or the
      // refocus history reload.
      const sid = activeSessionRef.current;
      if (data.type === "active_turn" && data.active === false) {
        setIsStreaming(false);
        setIsThinking(false);
        setIsAwaitingDetached(false);
        if (sid) fetchAuthoritativeHistory(sid).then(loadSessions).catch((err) => {
          console.error("completed duplicate refresh failed:", err);
        });
      } else {
        if (isDoppelganger && data.type === "active_turn") {
          cloudPollExhaustedSessionRef.current = null;
        }
        showActiveTurnPending(data.message);
        if (isDoppelganger && sid) startCloudPolling(sid);
        else if (sid && data.type === "active_turn") loadHistory(sid);
      }
    } else if (data.type === "notification") {
      // Unified ingestion: a producer (gmail / routine / reminder /
      // singularity / lupulus / compressor) wrote a message via
      // ChatService.inject_notification. Append to the open chat only if
      // it belongs to the active session; otherwise just refresh the
      // session sidebar so the new preview/timestamp shows up.
      const incomingSid = data.sessionId;
      if (incomingSid && incomingSid === activeSessionRef.current) {
        setIsAwaitingDetached(false);
        setMessages((prev) => {
          const retained = prev.filter((m) => !m.detachedNote);
          const incoming = {
            role: "assistant",
            content: data.content || "",
            timestamp: data.timestamp || new Date().toISOString(),
            sessionId: incomingSid,
            requestId: data.requestId,
            ...(data.messageId ? { messageId: data.messageId } : {}),
            kind: data.kind || "conversation",
            ...(data.source ? { source: data.source } : {}),
            ...(data.metadata ? { metadata: data.metadata } : {}),
          };
          const existing = data.requestId
            ? retained.findIndex((message) => message.role === "assistant" && message.requestId === data.requestId)
            : -1;
          if (existing < 0) return [...retained, incoming];
          const updated = [...retained];
          updated[existing] = { ...updated[existing], ...incoming, streaming: false, checking: null };
          return updated;
        });
      }
      loadSessions();
    } else if (data.type === "error") {
      setIsThinking(false);
      setIsStreaming(false);
      setIsAwaitingDetached(false);
      if (data.requestId) streamBuffers.current.delete(data.requestId);
      else streamBuffer.current = "";
      setMessages((prev) => finalizeTerminalErrorMessages(prev, data));

      const sessionId = data._sseSessionId || data.sessionId || activeSessionRef.current;
      void reconcileTerminalError(sessionId, data);
    }
  }, []);

  // Wire up streaming + load chat on mount.
  //
  // Instant-load UX: the input is usable in "second 1". We hydrate the
  // sidebar + active session's first page synchronously from the
  // localStorage cache (if present), then fire the sessions list and the
  // cached session's history over REST IN PARALLEL to reconcile.
  useEffect(() => {
    if (!token) return;
    chatMountedRef.current = true;

    transport.onMessage = handleWsMessage;
    transport.setToken(token);

    let unsubscribeNotifications = () => {};
    if (notifications) {
      notificationStream.connect(token);
      unsubscribeNotifications = notificationStream.subscribe(handleWsMessage);
    }

    // Restore streaming state if Anna was writing while we were away.
    if (transport._streamingActive || transport._streamingContent) {
      const rawContent = transport._streamingContent || "";
      const displayContent = rawContent.replace(/```action\s*\n[\s\S]*?```\s*/g, "").replace(/\n{3,}/g, "\n\n").trimEnd();
      streamBuffer.current = rawContent;
      if (transport._activeRequestId) {
        streamBuffers.current.set(transport._activeRequestId, rawContent);
      }
      setIsStreaming(transport._streamingActive);
      setIsThinking(transport._thinkingActive);
      if (displayContent) {
        setMessages([{
          role: "assistant",
          content: displayContent,
          timestamp: new Date().toISOString(),
          streaming: transport._streamingActive,
          requestId: transport._activeRequestId,
          ...(isDoppelganger ? { metadata: { assistantId: "doppelganger" } } : {}),
        }]);
      }
    }

    // Server freshness decides whether the restored chat may hydrate.
    loadSessions({ checkFreshness: true });

    // Refocus safety net: when the tab returns to the foreground, reload the
    // sidebar + active history so replies that completed while backgrounded
    // (detached turns, missed notification frames) show up.
    const onResume = () => {
      if (document.visibilityState && document.visibilityState !== "visible") return;
      if (transport._streamingActive) return; // live stream — don't wipe it
      if (notifications) notificationStream.connect(localStorage.getItem("anna_token"));
      loadSessions({ checkFreshness: true });
    };
    document.addEventListener("visibilitychange", onResume);
    window.addEventListener("focus", onResume);
    window.addEventListener("pageshow", onResume);

    return () => {
      chatMountedRef.current = false;
      document.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("focus", onResume);
      window.removeEventListener("pageshow", onResume);
      transport.onMessage = null;
      stopCloudPolling({ clearPending: true });
      // Keep the notification stream alive across mounts; just drop our handler.
      unsubscribeNotifications();
    };
  }, [token, handleWsMessage]);

  // Auto-scroll to bottom. We snap unconditionally after initial load,
  // session switch, or user send (shouldSnapRef). While streaming or
  // receiving notifications we only scroll if already near the bottom.
  useEffect(() => {
    const scrollToEnd = (behavior = "instant") => {
      const el = messagesContainerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    if (shouldSnapRef.current) {
      // Immediate scroll, then a follow-up after images/markdown finish layout
      scrollToEnd();
      const t = setTimeout(scrollToEnd, 120);
      return () => clearTimeout(t);
    }
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distanceFromBottom < 120) {
      scrollToEnd();
    }
  }, [messages, isThinking]);

  // Scroll-up pagination: load older messages when scrolling to top
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    // If user scrolled up away from bottom, stop forcing auto-snap
    const distFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    if (distFromBottom > 200) {
      shouldSnapRef.current = false;
    } else if (distFromBottom < 50) {
      shouldSnapRef.current = true;
    }
    const sessionId = activeSessionRef.current;
    if (!sessionId || loadingMore || !hasMore || !cursor) return;
    if (el.scrollTop < 80) {
      setLoadingMore(true);
      isPaginatingRef.current = true;
      loadHistory(sessionId, { before: cursor });
    }
  }, [loadingMore, hasMore, cursor]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!transport.isConfigured || (!input.trim() && attachedFiles.length === 0) || isStreaming || isAwaitingDetached || isUploading) return;
    const draftSessionId = activeSessionRef.current;
    const draftText = input;
    const draftFiles = [...attachedFiles];

    if (isDoppelganger && draftSessionId) {
      setIsStreaming(true);
      try {
        const status = await transport.activeTurn(draftSessionId);
        status.pendingTaskIds?.forEach((taskId) => pendingTaskIdsRef.current.add(taskId));
        if (status.pendingTasksKnown && !status.pendingTasks) pendingTaskIdsRef.current.clear();
        if (status.active) {
          setIsStreaming(false);
          cloudPollExhaustedSessionRef.current = null;
          showActiveTurnPending();
          startCloudPolling(draftSessionId);
          return;
        }
        if (status.pendingTasks || pendingTaskIdsRef.current.size) startCloudPolling(draftSessionId);
        await fetchAuthoritativeHistory(draftSessionId);
        if (status.pendingTasksKnown && !status.pendingTasks) pendingTaskIdsRef.current.clear();
        if (activeSessionRef.current !== draftSessionId) {
          setIsStreaming(false);
          return;
        }
      } catch (err) {
        console.error("pre-send cloud refresh failed:", err);
        setIsStreaming(false);
        setMessages((prev) => [...prev, {
          role: "system",
          content: "Could not refresh this cloud chat. Your draft was kept; please try again.",
          timestamp: new Date().toISOString(),
        }]);
        return;
      }
    }

    const requestId = createChatRequestId();
    const userMessage = {
      role: "user",
      content: draftText,
      timestamp: new Date().toISOString(),
      sessionId: draftSessionId,
      requestId,
      attachments: draftFiles.map((f) => ({
        filename: f.name,
        contentType: getAttachmentContentType(f),
        size: f.size,
      })),
    };
    shouldSnapRef.current = true;
    setIsAwaitingDetached(false);
    setMessages((prev) => [...prev, userMessage]);

    let attachmentsMeta = null;

    // Upload files if any
    if (draftFiles.length > 0) {
      setIsUploading(true);
      try {
        attachmentsMeta = await uploadChatAttachments(draftFiles);
      } catch (err) {
        console.error("File upload failed:", err);
        setMessages((prev) => [
          ...prev,
          { role: "system", content: `File upload failed: ${err.message}`, timestamp: new Date().toISOString() },
        ]);
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    if (activeSessionRef.current !== draftSessionId) return;
    const sessionId = activeSessionRef.current || `session_${Date.now()}`;
    if (!activeSessionRef.current) {
      updateActiveSession(sessionId);
      saveDraft(input, sessionId);
      setMessages((prev) => prev.map((message) => (
        message.requestId === requestId ? { ...message, sessionId } : message
      )));
    }
    const thinkNow = gemmaThinkRef.current;
    setIsStreaming(true);

    transport.send(draftText, sessionId, attachmentsMeta, {
      requestId,
      ...(isDoppelganger ? {} : { think: thinkNow }),
      onAccepted: () => {
        saveDraft("", sessionId);
        if (draftSessionId !== sessionId) saveDraft("", draftSessionId);
        setInput("");
        setAttachedFiles([]);
      },
    })
      .then((ok) => {
        if (!ok) {
          setIsStreaming(false);
          setIsThinking(false);
        }
        // Terminal frames own reconciliation. A strict backend may end with
        // `error` and no `done`, while successful turns refresh from `done`.
      });

  };

  const addFiles = useCallback((files) => {
    setAttachedFiles((prev) => mergeChatAttachments(prev, files));
  }, []);

  const handleInputPaste = useCallback(async (e) => {
    const pastedFiles = extractClipboardFiles(e.clipboardData);
    if (pastedFiles.length) {
      e.preventDefault();
      addFiles(pastedFiles);
      return;
    }
    if (clipboardHasInlineText(e.clipboardData)) {
      return;
    }
    const asyncFiles = await readClipboardFiles();
    if (!asyncFiles.length) return;
    e.preventDefault();
    addFiles(asyncFiles);
  }, [addFiles]);

  const handleEmojiSelect = useCallback((emoji) => {
    const textarea = textareaRef.current;
    const start = textarea?.selectionStart ?? input.length;
    const end = textarea?.selectionEnd ?? start;
    const next = `${input.slice(0, start)}${emoji}${input.slice(end)}`;
    setInput(next);
    saveDraft(next);
    requestAnimationFrame(() => {
      if (!textarea) return;
      const caret = start + emoji.length;
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, textarea.rows * 6 * 20) + "px";
    });
  }, [input]);

  // --- Voice recording ---
  const handleMicToggle = async () => {
    if (isRecording) {
      // Stop recording
      mediaRecorderRef.current?.stop();
      return;
    }

    // Check permission state first (if Permissions API available)
    if (navigator.permissions) {
      try {
        const perm = await navigator.permissions.query({ name: "microphone" });
        if (perm.state === "denied") {
          setMessages((prev) => [...prev, {
            role: "system",
            content: "Microphone access is blocked. Please go to your browser settings (click the lock/site icon in the address bar) and allow microphone access, then try again.",
            timestamp: new Date().toISOString(),
          }]);
          return;
        }
      } catch (_) {
        // Permissions API may not support "microphone" query in all browsers — continue
      }
    }

    // Start recording — this triggers the browser permission popup if not yet granted
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick a supported mime type (Safari doesn't support webm)
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"].find(
        (m) => MediaRecorder.isTypeSupported(m)
      ) || "";
      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      const ext = mimeType.includes("mp4") ? "mp4" : mimeType.includes("ogg") ? "ogg" : "webm";
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        if (blob.size === 0) return;
        setIsTranscribing(true);
        try {
          const reader = new FileReader();
          const base64 = await new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const API_BASE = import.meta.env.VITE_API_URL || "/api";
          const token = localStorage.getItem("anna_token");
          const res = await fetch(`${API_BASE}/voice`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ audio: base64, language: "es", ext }),
          });
          const data = await res.json();
          if (data.text) {
            setInput((prev) => {
              const next = prev ? prev + " " + data.text : data.text;
              saveDraft(next);
              return next;
            });
          } else if (data.error) {
            setMessages((prev) => [...prev, { role: "system", content: `Voice error: ${data.error}`, timestamp: new Date().toISOString() }]);
          }
        } catch (err) {
          console.error("Transcription failed:", err);
          setMessages((prev) => [...prev, { role: "system", content: "Voice transcription failed. Check your connection.", timestamp: new Date().toISOString() }]);
        } finally {
          setIsTranscribing(false);
        }
      };
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Mic access denied:", err);
      setMessages((prev) => [...prev, { role: "system", content: "Microphone access denied. Please allow mic access in your browser and macOS System Settings > Privacy & Security > Microphone.", timestamp: new Date().toISOString() }]);
    }
  };

  const handleStop = () => {
    transport.stop();
    stopCloudPolling({ clearPending: true });
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    setMessages((prev) => {
      const updated = [...prev];
      const last = updated[updated.length - 1];
      if (last?.streaming) {
        updated[updated.length - 1] = { ...last, streaming: false };
      }
      return updated;
    });
    streamBuffer.current = "";
    streamBuffers.current.clear();
  };

  const handleNewChat = () => {
    updateActiveSession(null);
    shouldSnapRef.current = true;
    setMessages([]);
    setHasMore(false);
    setCursor(null);
    setLoadingMore(false);
    isPaginatingRef.current = false;
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    streamBuffer.current = "";
    streamBuffers.current.clear();
  };

  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation();
    if (confirmDeleteId === sessionId) {
      // Confirmed — actually delete
      setConfirmDeleteId(null);
      chatApi.deleteSession(sessionId).catch((err) =>
        console.error("deleteSession failed:", err)
      );
      if (activeSessionRef.current === sessionId) {
        updateActiveSession(null);
        setMessages([]);
      }
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } else {
      // First click — ask for confirmation
      setConfirmDeleteId(sessionId);
    }
  };

  const handleCancelDelete = (e) => {
    e.stopPropagation();
    setConfirmDeleteId(null);
  };

  const handleSelectSession = (sessionId) => {
    if (sessionId === activeSession) return;
    updateActiveSession(sessionId);
    shouldSnapRef.current = true;
    setMessages([]);
    setHasMore(false);
    setCursor(null);
    setIsStreaming(false);
    setIsThinking(false);
    setIsAwaitingDetached(false);
    streamBuffer.current = "";
    streamBuffers.current.clear();
    // Request last 10 messages for this session (REST, cache-first).
    loadHistory(sessionId);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  const formatSessionDate = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return "Today";
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  return (
    <div className="chat-page">
      {/* Floating sidebar toggle for mobile */}
      {(
        <button
          className={`chat-sidebar-fab ${showSidebar ? "fab-active" : ""}`}
          onClick={() => setShowSidebar(!showSidebar)}
          title={showSidebar ? "Hide chats" : "Show chats"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}
      {/* Backdrop for mobile sidebar */}
      {showSidebar && <div className="chat-sidebar-backdrop" onClick={() => setShowSidebar(false)} />}
      {/* Chat sessions sidebar */}
      {showSidebar && (
        <aside className="chat-sidebar open">
          <button className="btn-primary chat-newchat-btn" onClick={handleNewChat}>
            + New Chat
          </button>
          <div className="chat-sessions">
            {sessions.length === 0 && <p className="chat-empty">No conversations yet</p>}
            {sessions.filter((s) => s.id).map((s) => (
              <div
                key={s.id}
                className={`chat-session ${s.id === activeSession ? "active" : ""}`}
                onClick={() => handleSelectSession(s.id)}
                role="button"
                tabIndex={0}
              >
                <div className="chat-session-title">
                  {s.source === "telegram" && <span className="session-source-badge telegram">TG</span>}
                  {s.source === "routine" && <span className="session-source-badge routine">RT</span>}
                  {s.preview || "New conversation"}
                </div>
                <div className="chat-session-meta">{formatSessionDate(s.updatedAt)}</div>
                {confirmDeleteId === s.id ? (
                  <span className="session-delete-confirm">
                    <button
                      className="btn-icon confirm-yes"
                      onClick={(e) => handleDeleteSession(e, s.id)}
                      title="Confirm delete"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon confirm-no"
                      onClick={handleCancelDelete}
                      title="Cancel"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </span>
                ) : (
                  <button
                    className="chat-session-delete"
                    onClick={(e) => handleDeleteSession(e, s.id)}
                    title="Delete chat"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </aside>
      )}

      {/* Main chat area */}
      <div className="chat-main">
        {(
          <button
            type="button"
            className="chat-sidebar-toggle"
            onClick={() => setShowSidebar((v) => !v)}
            title={showSidebar ? "Hide sessions" : "Show sessions"}
          >
            {showSidebar ? "‹" : "›"}
          </button>
        )}
        <div className="chat-header">
          <div className="chat-header-left" />
          {<div className="chat-header-right">
            {!isDoppelganger && <button
              className={`btn-think-toggle ${gemmaThink ? "active" : ""}`}
              onClick={() => {
                const next = !gemmaThink;
                setGemmaThink(next);
                gemmaThinkRef.current = next;
                localStorage.setItem("anna_gemma_think", String(next));
                console.log("[chat] gemma think toggled to:", next);
              }}
              title={gemmaThink
                ? "Thinking enabled — click to disable"
                : "Thinking disabled — click to enable"}
            >
              {gemmaThink ? "💭 Think" : "💭 Off"}
            </button>}
            <button
              type="button"
              className={`btn-voice-toggle ${voiceEnabled ? "active" : ""} ${isSpeaking ? "speaking" : ""}`}
              onClick={toggleVoice}
              aria-label="Voice responses"
              aria-pressed={voiceEnabled}
              title={voiceEnabled ? "Voice responses on: mute" : "Voice responses off: enable"}
            >
              {voiceEnabled ? <Volume2 size={21} aria-hidden="true" /> : <VolumeX size={21} aria-hidden="true" />}
            </button>
            <button type="button" className="chat-new-chat" onClick={handleNewChat} title="New chat" aria-label="New chat">
              <SquarePen size={21} aria-hidden="true" />
            </button>
          </div>}
        </div>
        {<>
          {!transport.isConfigured && (
            <div className="chat-configuration-error" role="status">
              Anna is temporarily unavailable.
            </div>
          )}
          <div className={`chat-messages${messages.length === 0 && !isThinking ? " chat-messages-empty" : ""}`} ref={messagesContainerRef} onScroll={handleMessagesScroll}>
          {loadingMore && (
            <div className="loading-more">Loading older messages...</div>
          )}
          {hasMore && !loadingMore && (
            <div className="loading-more faded">Scroll up for more</div>
          )}
          {messages.length === 0 && !isThinking && (
            <div className="chat-welcome">
              <AnnaIdentity />
            </div>
          )}
          {groupActionNotes(
            messages
              .filter((msg) => !(msg.role === "user" && msg.source === "routine"))
              .filter((msg) => msg.source !== "email_triage")
          ).map((msg, i) => (
            <MessageBubble key={i} m={msg} variant="full" />
          ))}
          {isThinking && !messages.some((m) => m.streaming) && (
            <div className="message assistant thinking">
              <div className="message-role">{assistantName}</div>
              <div className="message-body">
                <div className="thinking-indicator">
                  <span className="thinking-dot"></span>
                  <span className="thinking-dot"></span>
                  <span className="thinking-dot"></span>
                </div>
                <span className="thinking-text">{assistantName} is thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <form className="chat-input" onSubmit={handleSend}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false); }}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            const dropped = Array.from(e.dataTransfer.files);
            addFiles(dropped);
          }}
        >
          {isDragOver && (
            <div className="drag-overlay">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <span>Drop files here</span>
            </div>
          )}
          {attachedFiles.length > 0 && (
            <div className="attachment-preview-strip">
              {attachedFiles.map((f, i) => (
                <div key={i} className="attachment-chip">
                  {getAttachmentContentType(f).startsWith("image/") ? (
                    <img src={URL.createObjectURL(f)} alt={f.name} className="attachment-chip-thumb" />
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                  )}
                  <span className="attachment-chip-name">{f.name}</span>
                  <button type="button" className="attachment-chip-remove" onClick={() => setAttachedFiles((prev) => prev.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv,text/markdown,text/x-python,text/javascript,text/typescript,text/html,text/css,application/json,application/xml,text/xml,text/yaml,application/x-yaml,.py,.js,.jsx,.ts,.tsx,.html,.htm,.css,.json,.xml,.yaml,.yml,.sql,.sh,.ino,.cpp,.cc,.cxx,.c,.h,.hpp,.swift,.java,.go,.rs"
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
          <div className="chat-input-row">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                if (e.target.value) {
                  saveDraft(e.target.value);
                } else {
                  saveDraft("");
                }
                e.target.style.height = "auto";
                e.target.style.height = Math.min(e.target.scrollHeight, e.target.rows * 6 * 20) + "px";
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
                  e.preventDefault();
                  handleSend(e);
                }
              }}
              onPaste={handleInputPaste}
              placeholder={!transport.isConfigured ? "Anna is temporarily unavailable" : isUploading ? "Uploading files..." : isTranscribing ? "Transcribing..." : isRecording ? "Recording... click mic to stop" : isAwaitingDetached ? `Waiting for ${assistantName} to finish...` : isStreaming ? `${assistantName} is responding...` : `Ask ${assistantName} anything...`}
              disabled={!transport.isConfigured || isStreaming || isAwaitingDetached}
              autoFocus
              rows={2}
            />
            <div className="chat-input-tools">
              <EmojiPicker
                compact
                onSelect={handleEmojiSelect}
                disabled={!transport.isConfigured || isStreaming || isAwaitingDetached}
              />
              <button
                type="button"
                className="btn-attach"
                onClick={() => fileInputRef.current?.click()}
                disabled={!transport.isConfigured || isStreaming || isAwaitingDetached || isUploading}
                title="Attach files"
                aria-label="Attach files"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>
              <button
                type="button"
                className={`btn-mic ${isRecording ? "recording" : ""} ${isTranscribing ? "transcribing" : ""}`}
                onClick={handleMicToggle}
                disabled={!transport.isConfigured || isStreaming || isAwaitingDetached || isTranscribing}
                title={isRecording ? "Stop recording" : "Voice input"}
                aria-label={isRecording ? "Stop recording" : "Voice input"}
              >
                {isTranscribing ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" strokeDasharray="31.4" strokeDashoffset="10" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="1" width="6" height="11" rx="3" />
                    <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
                    <line x1="12" y1="18" x2="12" y2="23" />
                    <line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                )}
              </button>
            </div>
            {isStreaming || isAwaitingDetached ? (
              <button type="button" className="btn-stop" onClick={handleStop} title="Stop" aria-label="Stop">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                className="btn-send"
                disabled={!transport.isConfigured || isAwaitingDetached || (!input.trim() && attachedFiles.length === 0)}
                title="Send"
                aria-label="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            )}
          </div>
          </form>
        </>}
      </div>
    </div>
  );
}

export default Chat;
